import { onBeforeUnmount, onMounted, ref, Ref } from 'vue'

/** On small screens WebLayout pins the search bar at the top (class `fixed`, 5rem tall) once it scrolls past it. */
const MOBILE_SEARCH_QUERY = '(max-width: 600px)'
const MOBILE_SEARCH_HEIGHT = 80

/**
 * Height covered by the store's fixed header (fixed nav and/or the pinned mobile search)
 * when the page is scrolled to `targetScrollY`.
 */
export function fixedHeaderOffset(targetScrollY = window.scrollY): number {
    let offset = 0
    const nav = document.getElementById('nav')
    if (nav) {
        const style = getComputedStyle(nav)
        if (style.position === 'fixed' && style.display !== 'none') offset = Math.max(offset, nav.getBoundingClientRect().bottom)
    }
    const search = document.querySelector('.web-layout__search') as HTMLElement | null
    if (search && search.getClientRects().length) {
        if (search.classList.contains('fixed')) {
            // The pinned search animates its height (transition .2s): use its final height, otherwise a
            // value measured mid-animation leaves sticky bars partly hidden behind it until the next scroll.
            offset = Math.max(offset, search.getBoundingClientRect().bottom, MOBILE_SEARCH_HEIGHT)
        } else if (window.matchMedia(MOBILE_SEARCH_QUERY).matches) {
            const searchTop = search.getBoundingClientRect().top + window.scrollY
            if (targetScrollY >= searchTop) offset = Math.max(offset, MOBILE_SEARCH_HEIGHT)
        }
    }
    return Math.max(0, Math.round(offset))
}

/** Smooth-scrolls so `el` lands right below the fixed header plus `extra` px (e.g. a sticky bar). */
export function scrollToElement(el: HTMLElement, extra = 0) {
    const docTop = el.getBoundingClientRect().top + window.scrollY
    // two passes: the header height may depend on where we end up (pinned mobile search)
    let y = docTop - fixedHeaderOffset(docTop) - extra
    y = docTop - fixedHeaderOffset(y) - extra
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
}

/** Current fixed header height, kept up to date on scroll/resize (for `position: sticky; top`). */
export function useStickyTop(): Ref<number> {
    const top = ref(0)
    let frame = 0
    const update = () => {
        frame = 0
        top.value = fixedHeaderOffset()
    }
    const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update)
    }
    onMounted(() => {
        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)
    })
    onBeforeUnmount(() => {
        window.removeEventListener('scroll', schedule)
        window.removeEventListener('resize', schedule)
        if (frame) cancelAnimationFrame(frame)
    })
    return top
}

/**
 * Scroll-spy over the `[data-sf-category]` blocks inside `container`: the active category is the
 * topmost one crossing the band between `topOffset` and ~45% of the viewport.
 * Call `observe()` whenever the blocks or the offset change.
 */
export function useScrollSpy(container: Ref<HTMLElement | null>, topOffset: () => number) {
    const activeId = ref<string | null>(null)
    const visible = new Map<string, boolean>()
    let observer: IntersectionObserver | null = null
    let lockUntil = 0
    let lockTimer: ReturnType<typeof setTimeout> | undefined

    const blocks = (): HTMLElement[] => Array.from(container.value?.querySelectorAll<HTMLElement>('[data-sf-category]') ?? [])

    function pickActive() {
        if (Date.now() < lockUntil) return
        const els = blocks()
        if (!els.length) return
        const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
        const current = atBottom ? els[els.length - 1] : els.find((el) => visible.get(el.dataset.sfCategory as string))
        if (current) activeId.value = current.dataset.sfCategory as string
    }

    function disconnect() {
        observer?.disconnect()
        observer = null
        visible.clear()
    }

    function observe() {
        disconnect()
        const els = blocks()
        if (!els.length) return
        if (!activeId.value || !els.some((el) => el.dataset.sfCategory === activeId.value)) activeId.value = els[0].dataset.sfCategory as string
        if (typeof IntersectionObserver === 'undefined') return
        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => visible.set((entry.target as HTMLElement).dataset.sfCategory as string, entry.isIntersecting))
            pickActive()
        }, { rootMargin: `-${Math.max(0, Math.round(topOffset()))}px 0px -55% 0px`, threshold: 0 })
        els.forEach((el) => observer?.observe(el))
    }

    /** Marks `id` active and ignores the spy while a programmatic smooth scroll runs. */
    function lock(id: string, ms = 900) {
        activeId.value = id
        lockUntil = Date.now() + ms
        clearTimeout(lockTimer)
        lockTimer = setTimeout(pickActive, ms + 50)
    }

    onBeforeUnmount(() => {
        disconnect()
        clearTimeout(lockTimer)
    })

    return { activeId, observe, disconnect, lock }
}
